import { routesDistribution } from './features/distribution'
import { PageBilletRemis } from './features/distribution/Public'
import { routesAcces } from './features/acces'
import { routesPartenairesAdmin, routesPartenaireOperateur } from './features/partenaires'
import { routesCommunication, routesPreferencePublique } from './features/communication'
import { LanguePublique, LangueMaison, LangueVitrine } from './components/LangueInterface'
import { routesFinance } from './features/finance'
/**
 * Routage — trois espaces, un seul artefact (ARCHITECTURE §9.1).
 *
 *   /admin/*    back-office     session staff, rôle selon l’écran
 *   /scan/*     contrôle d’accès session staff (admin, guichet ou contrôle)
 *   /portail/*  abonné          session abonné, lecture seule
 *   /           redirige selon la session
 *
 * **Contrat avec les agents de module** : chaque dossier de `src/features/`
 * exporte un tableau `routes<Module>` de `RouteObject`. Le routeur n’importe que
 * ce tableau et le monte sous la bonne garde. Un agent de module ajoute donc ses
 * écrans **sans jamais modifier ce fichier**, qui appartient à la fondation
 * (ARCHITECTURE §11) — exactement le pendant frontend de `backend/src/app.ts`.
 *
 * Les gardes ci-dessous sont un confort d’interface : l’autorisation qui fait
 * foi est celle du backend (préhandlers `requireRole`).
 */

import { lazy, Suspense, useEffect, useState, type ComponentType } from 'react'
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
  type RouteObject,
} from 'react-router'

import {
  ConnexionAbonne,
  ConnexionStaff,
  RequireAbonne,
  RequireStaff,
  useAuth,
  useRole,
  VerificationLienMagique,
} from './auth'
// Imports directs (pas le barrel `./components`) : ce barrel réexporte aussi
// `SeatMap`, un gros composant que seules des routes lazy utilisent — passer
// par lui ici entraînerait tout `SeatMap` dans le chunk initial.
import { ErreurRoute } from './components/ErreurRoute'
import { LayoutAdmin } from './components/LayoutAdmin'
import { LayoutPortail } from './components/LayoutPortail'
import { LayoutScan } from './components/LayoutScan'
import { PageIntrouvable } from './components/PageIntrouvable'
// Import direct (pas le barrel `./features/accueil-theatre`), pour la même
// raison : ce barrel réexporte `PageAccueilTheatre`, que la racine charge en
// lazy. `racine.ts` n'est, lui, qu'une fonction pure de trois lignes.
import { adresseSansTheatre } from './features/accueil-theatre/racine'
/* LOT B — la documentation. Le chemin (`CHEMIN_AIDE`) est exporté par la
   feature, pour la même raison que `CHEMIN_APPARENCE` et `CHEMIN_REGIE` :
   `lib/chemins.ts` appartient à la fondation, et un lot voisin y écrit. */
import { CHEMIN_AIDE, routesAide } from './features/aide'
import { routesApparence } from './features/apparence'
import { LayoutBoutique, routesBoutique } from './features/boutique'
import { routesCampagne } from './features/campagne'
import { routesCatalogue, routesIncidents } from './features/catalogue'
import { routesConfiguration } from './features/configuration'
// LOT F1 — voir la note en pied de fichier : la console SUBSTITUE son arbre.
import { estHoteConsole, routesConsole } from './features/console'
import { routesDashboards, routesPilotage } from './features/dashboards'
/* SPEC-BOUTIQUE §7 — la console de régie. Le chemin complet (`CHEMIN_REGIE`)
   est exporté par la feature, pour la même raison que `CHEMIN_APPARENCE`. */
import { routesDeblocage } from './features/deblocage'
import { routesPlanning } from './features/planning'
import { routesFormules } from './features/formules'
import { routesImports } from './features/imports'
/* LOT F2 — l'inscription en démonstration. Montée à la racine, sans garde :
   elle s'adresse à quelqu'un qui n'a pas encore de théâtre. */
import { CHEMIN_INSCRIPTION, routesInscription } from './features/inscription'
import { routesPaiements } from './features/paiements'
import { routesPanier } from './features/panier'
import { routesPortail } from './features/portail'
import { routesScan } from './features/scan'
import { routesSouscriptions } from './features/souscriptions'
import { routesTarification } from './features/tarification'
/* R15 — l'avertissement d'environnement de test. Non paresseux : il doit être
   là au premier rendu, y compris sur un écran de connexion. Hors UAT il ne
   trouve pas de bandeau et ne rend rien — le coût est nul. */
import { ModaleUat } from './features/uat/ModaleUat'
import { routesVentes } from './features/ventes'
import { accueilStaff, CHEMINS } from './lib/chemins'
import { estHoteMarketingConnu } from './lib/hoteMarketing'
import { useTheatrePublique } from './lib/theatre'
import { ChargementPage } from './ui/Spinner'

// Les deux pages publiques de la racine : chargées à la demande, elles
// n’entrent pas dans le chunk initial que téléchargent le personnel et les
// abonnés connectés. Une seule des deux est jamais téléchargée par un visiteur
// donné — elles ne cohabitent pas sur une même adresse.
const PageVitrine = lazy(async () => {
  const module = await import('./features/vitrine')
  return { default: module.PageVitrine }
})
let PageVitrinePrechargee: ComponentType | null = null

/** Garde le premier écran HTML en place jusqu'à ce que la vitrine soit prête. */
export async function preparerPageVitrine(): Promise<void> {
  const module = await import('./features/vitrine')
  PageVitrinePrechargee = module.PageVitrine
}
const PageAccueilTheatre = lazy(async () => {
  const module = await import('./features/accueil-theatre')
  return { default: module.PageAccueilTheatre }
})

/* -------------------------------------------------------------------------- */
/* Redirections                                                               */
/* -------------------------------------------------------------------------- */

/**
 * `/` — la racine appartient au public. Une session active n’y a rien à faire :
 * le personnel repart sur `/admin`, l’abonné sur `/portail`.
 *
 * ★ **Un visiteur, deux pages possibles — c’est l’ADRESSE qui tranche**
 * (SPEC-BOUTIQUE §8 bis D bis) :
 *
 *   `ticket.ilygo.ch`          → la vitrine du PRODUIT, pour une direction de
 *                                théâtre qui cherche un logiciel ;
 *   `<slug>.ticket.ilygo.ch`   → la page d’accueil DU THÉÂTRE, pour ses
 *                                spectateurs, ses abonnés et son personnel.
 *
 * La distinction ne se lit PAS dans `location.hostname` : `localhost:5173` est
 * l’apex en développement multi-tenant et l’adresse du théâtre en mono-tenant.
 * Elle vient du backend, qui la connaît déjà — voir
 * `features/accueil-theatre/racine.ts`, qui porte tout le raisonnement.
 */
export function RedirectionRacine() {
  const { session, chargement } = useAuth()
  const hoteMarketing = estHoteMarketingConnu(globalThis.location?.hostname ?? '')
  /* La sonde ne part que pour un visiteur : le personnel et les abonnés
     repartent aussitôt vers leur espace, où leur coque la demandera de toute
     façon (même clé, donc dédupliquée par TanStack Query). */
  const visiteur = !chargement && session === null
  const theatre = useTheatrePublique(visiteur && !hoteMarketing)
  const [vitrineConfirmee, confirmerVitrine] = useState(false)
  const vitrineDetectee = hoteMarketing || adresseSansTheatre(theatre.error)

  // Le 404 TENANT_INCONNU identifie l'apex pour toute la vie de cette page.
  // Une relance de la sonde peut repasser par un état pending : conserver la
  // vitrine montée évite de remettre le défilement du visiteur à zéro.
  useEffect(() => {
    if (vitrineDetectee) confirmerVitrine(true)
  }, [vitrineDetectee])

  // Sur ces hôtes connus, aucun théâtre ni session ne peut posséder la racine.
  // Le chargement de /auth/moi ne doit pas masquer la vitrine.
  if (chargement && !hoteMarketing) return <ChargementPage />

  if (session === null) {
    if (!hoteMarketing && theatre.isPending && !vitrineConfirmee) return <ChargementPage />
    const Page =
      vitrineDetectee || vitrineConfirmee
        ? (PageVitrinePrechargee ?? PageVitrine)
        : PageAccueilTheatre
    const CadreLangue = vitrineDetectee || vitrineConfirmee ? LangueVitrine : LanguePublique
    return (
      <CadreLangue>
        <Suspense fallback={<ChargementPage />}>
          <Page />
        </Suspense>
      </CadreLangue>
    )
  }

  return (
    <Navigate
      to={session.sujet === 'staff' ? accueilStaff(session.utilisateur.role) : CHEMINS.portail}
      replace
    />
  )
}

/** `/admin` — chacun arrive sur l’écran de son métier. */
function AccueilAdmin() {
  const role = useRole()
  return <Navigate to={role === null ? CHEMINS.tableaux : accueilStaff(role)} replace />
}

/* -------------------------------------------------------------------------- */
/* Arbre des routes                                                           */
/* -------------------------------------------------------------------------- */

/**
 * ★ L’arbre des routes du produit — **exporté, et c’est délibéré**.
 *
 * Le garde-fou de rendu (`lib/ecrans-vocabulaire.test.tsx`) dresse l’inventaire
 * des écrans à vérifier en PARCOURANT cet arbre, au lieu de recopier une liste
 * à la main. Une liste recopiée se périme au premier écran ajouté — et c’est
 * exactement le défaut que ce garde-fou répare : un écran que personne n’a
 * pensé à lister est quand même monté ici, donc quand même rendu, donc quand
 * même vérifié.
 *
 * L’export ne change rien à l’exécution : `router` reste la seule chose que
 * `App.tsx` consomme.
 */
export const routes: RouteObject[] = [
  {path:'/billets/remis',element:<LanguePublique><PageBilletRemis/></LanguePublique>},
  {path:'/billets/transfert',element:<LanguePublique><PageBilletRemis transfert/></LanguePublique>},
  {path:'/communications/preferences',element:<LanguePublique><Outlet/></LanguePublique>,children:routesPreferencePublique},
  {
    path: CHEMINS.racine,
    element: <RedirectionRacine />,
    errorElement: <ErreurRoute />,
  },

  /* ------------------------------------------------------------- boutique */
  /**
   * ★ Le seul espace SANS garde (BOU-10 : « aucun compte à créer pour
   * acheter »). Il est monté avant `/admin` pour que la lecture du fichier
   * suive celle du produit : d’abord ce qu’un inconnu voit, ensuite ce qui
   * demande une session.
   *
   * `LayoutBoutique` n’est PAS chargé paresseusement, contrairement à ses
   * pages : c’est lui qui porte le compte à rebours du panier (REZ-02), et un
   * minuteur qui apparaîtrait une demi-seconde après la page donnerait
   * l’impression d’un temps qui saute.
   */
  {
    path: CHEMINS.boutique,
    element: (
      <LanguePublique>
        <LayoutBoutique />
      </LanguePublique>
    ),
    errorElement: <ErreurRoute />,
    children: routesBoutique,
  },

  /* ----------------------------------------------------- inscription démo */
  /**
   * ★ Le second espace SANS garde, après la boutique — et le seul qui existe
   * AVANT un théâtre. Un visiteur y crée le sien : il n'a donc ni session, ni
   * compte, ni sous-domaine. Le monter sous `/admin` reviendrait à demander
   * une clé à quelqu'un qui vient chercher la porte.
   *
   * Les pages sont chargées paresseusement, et leur `Suspense` est celui du
   * routeur : aucune d'elles n'entre dans le lot initial que téléchargent le
   * personnel et les abonnés.
   */
  {
    path: CHEMIN_INSCRIPTION,
    errorElement: <ErreurRoute />,
    children: routesInscription,
  },

  /* ------------------------------------------------------------------ aide */
  /**
   * ★ Le TROISIÈME espace SANS garde — la documentation (chantier L).
   *
   * Elle est montée ici, à la racine et hors de toute garde, pour la même
   * raison que la boutique : la moitié de ses pages s’adresse à des gens qui
   * n’ont pas de compte et n’en auront jamais. Quelqu’un qui cherche comment
   * retrouver son billet ne doit pas se connecter pour lire la réponse.
   *
   * L’adresse est servie depuis le sous-domaine d’une salle **comme depuis
   * l’apex** : c’est le même artefact, et le menu de la vitrine du produit peut
   * donc y renvoyer sans que la documentation devienne un second site à
   * republier.
   *
   * Coque et pages sont chargées paresseusement (`features/aide/index.tsx`) :
   * une trentaine de pages de texte n’entrent pas dans le lot initial.
   */
  {
    path: CHEMIN_AIDE,
    errorElement: <ErreurRoute />,
    children: routesAide,
  },

  /* ---------------------------------------------------------------- admin */
  {
    path: CHEMINS.connexionStaff,
    element: <ConnexionStaff />,
    errorElement: <ErreurRoute />,
  },
  {
    path: CHEMINS.admin,
    element: (
      <LangueMaison>
        <RequireStaff>
          <LayoutAdmin />
        </RequireStaff>
      </LangueMaison>
    ),
    errorElement: <ErreurRoute />,
    children: [
      { index: true, element: <AccueilAdmin /> },
      {path:'partenaires',element:<RequireStaff roles={['admin']}/>,children:routesPartenairesAdmin},
      {path:'distribution',element:<RequireStaff roles={['admin','guichet']}/>,children:routesDistribution},
      {path:'acces',element:<RequireStaff roles={['admin']}/>,children:routesAcces},
      {path:'crm',element:<RequireStaff roles={['admin']}/>,children:routesCommunication},
      {path:'partenaire',element:<RequireStaff roles={['partenaire']}/>,children:routesPartenaireOperateur},
      {
        path: 'finance',
        element: <RequireStaff roles={['admin', 'guichet']} />,
        children: routesFinance,
      },

      // Configuration et catalogue : administration uniquement.
      {
        path: 'configuration',
        element: <RequireStaff roles={['admin']} />,
        children: routesConfiguration,
      },
      {
        /* PER-01…PER-07 — l’apparence du théâtre. Écran d’administration :
           changer la couleur d’un tenant se voit de tout le public.
           Le chemin complet (`CHEMIN_APPARENCE`) est exporté par la feature —
           `lib/chemins.ts` appartient à la fondation, et une boutique en
           chantier y écrit en ce moment. */
        path: 'apparence',
        element: <RequireStaff roles={['admin']} />,
        children: routesApparence,
      },
      {
        path: 'imports',
        element: <RequireStaff roles={['admin']} />,
        children: routesImports,
      },
      {
        path: 'planning',
        element: <RequireStaff roles={['admin', 'guichet', 'lecture']} />,
        children: routesPlanning,
      },
      {
        path: 'catalogue',
        element: <RequireStaff roles={['admin']} />,
        children: routesCatalogue,
      },
      {
        path: 'incidents',
        element: <RequireStaff roles={['admin', 'guichet']} />,
        children: routesIncidents,
      },
      {
        path: 'tarification',
        element: <RequireStaff roles={['admin']} />,
        children: routesTarification,
      },
      {
        path: 'formules',
        element: <RequireStaff roles={['admin']} />,
        children: routesFormules,
      },

      {
        /* ★ SPEC-BOUTIQUE §7.5 — « réservées au rôle administrateur du tenant,
           pas au guichet ». La garde qui fait foi est celle du plugin backend
           (`requireRole('admin')` sur tout `/deblocage/*`) ; celle-ci évite au
           guichet de découvrir des écrans qui lui répondraient 403. */
        path: 'regie',
        element: <RequireStaff roles={['admin']} />,
        children: routesDeblocage,
      },

      // Campagne et guichet : administration et guichet.
      {
        path: 'campagne',
        element: <RequireStaff roles={['admin', 'guichet']} />,
        children: routesCampagne,
      },
      {
        path: 'souscriptions',
        element: <RequireStaff roles={['admin', 'guichet']} />,
        children: routesSouscriptions,
      },
      {
        path: 'ventes',
        element: <RequireStaff roles={['admin', 'guichet']} />,
        children: routesVentes,
      },
      {
        path: 'paniers',
        element: <RequireStaff roles={['admin', 'guichet']} />,
        children: routesPanier,
      },
      {
        path: 'paiements',
        element: <RequireStaff roles={['admin', 'guichet', 'lecture']} />,
        children: routesPaiements,
      },

      // Les chiffres de la maison : direction (lecture) et administration.
      {path:'pilotage',element:<RequireStaff roles={['admin','lecture']}/>,children:routesPilotage},
      // Liste explicite, pas de hiérarchie : le guichet n’y entre pas.
      {
        path: 'tableaux',
        element: <RequireStaff roles={['admin', 'lecture']} />,
        children: routesDashboards,
      },
    ],
  },

  /* ----------------------------------------------------------------- scan */
  {
    path: CHEMINS.scan,
    element: (
      <RequireStaff roles={['admin', 'guichet', 'controle']}>
        <LayoutScan />
      </RequireStaff>
    ),
    errorElement: <ErreurRoute />,
    children: routesScan,
  },

  /* -------------------------------------------------------------- portail */
  {
    path: CHEMINS.connexionAbonne,
    element: (
      <LanguePublique>
        <ConnexionAbonne />
      </LanguePublique>
    ),
    errorElement: <ErreurRoute />,
  },
  {
    path: CHEMINS.verificationLienMagique,
    element: (
      <LanguePublique>
        <VerificationLienMagique />
      </LanguePublique>
    ),
    errorElement: <ErreurRoute />,
  },
  {
    path: CHEMINS.portail,
    element: (
      <LanguePublique>
        <RequireAbonne>
          <LayoutPortail />
        </RequireAbonne>
      </LanguePublique>
    ),
    errorElement: <ErreurRoute />,
    children: routesPortail,
  },

  /* ------------------------------------------ démonstration du SeatMap */
  // Chargée à la demande et montée en développement seulement : c’est un banc
  // d’essai du composant pivot, pas un écran du produit.
  ...(import.meta.env.DEV
    ? [
        {
          path: CHEMINS.demoPlan,
          lazy: async () => {
            const module = await import('./components/SeatMap/SeatMap.demo')
            return { Component: module.SeatMapDemo }
          },
        } satisfies RouteObject,
      ]
    : []),

  { path: '*', element: <PageIntrouvable /> },
]

/**
 * ★ LOT F1 — la console d’exploitation **remplace** l’arbre ci-dessus quand la
 * page est servie depuis son hôte (`console.ticket.ilygo.ch`, `console.localhost`
 * en développement). Ce n’est pas un espace de plus à côté d’`/admin`, `/scan`
 * et `/portail` : c’est une substitution, et elle reflète exactement ce que fait
 * le backend, où une route de portée `console` répond 404 depuis l’adresse d’un
 * théâtre et réciproquement (`plateforme/hooks.ts`). Les deux couches décident la
 * même chose à partir du même signal — l’en-tête `Host`.
 */
/**
 * ★★ LA RACINE SANS CHEMIN — elle ne sert qu'à poser le défilement, UNE FOIS.
 *
 * Le défaut, constaté au navigateur : ouvrir la fiche d'une soirée depuis le
 * BAS d'une longue liste faisait apparaître la fiche **au milieu de son plan de
 * salle**, titre et compteurs hors écran. Le navigateur conserve la position de
 * défilement quand l'adresse change ; personne ne la remettait à zéro.
 *
 * ★ Pourquoi ici et pas dans l'écran concerné : le défaut n'est pas propre à
 *   cette fiche, il vaut pour les quinze autres. Le corriger là où on l'a vu
 *   aurait laissé les quatorze suivantes, et c'est très exactement le motif que
 *   ce projet a passé six vagues à rattraper. Une route pathless enveloppe tout
 *   l'arbre : on le pose une fois, il vaut partout, et un écran ajouté demain en
 *   hérite sans que personne y pense.
 *
 * ★ `ScrollRestoration` ne remet PAS bêtement à zéro : il mémorise la position
 *   de chaque entrée d'historique et la REPOSE au retour arrière. C'est la
 *   moitié qui compte autant que l'autre : arriver en haut d'une fiche est
 *   juste, mais revenir à la liste et se retrouver en haut au lieu de retrouver
 *   sa ligne serait un second défaut, plus agaçant que le premier.
 *
 * La console garde son propre arbre (voir ci-dessous) et reçoit le même
 * traitement : elle a des listes de tenants qui se parcourent pareil.
 */
function Racine() {
  return (
    <>
      <ScrollRestoration />
      {/* ★ R15 — la modale d'environnement de test, montée pour TOUT l'arbre.
          Même raisonnement que `ScrollRestoration` juste au-dessus : la
          convention exige l'avertissement sur toutes les pages, y compris les
          écrans de connexion et les pages d'erreur. Le poser sur l'accueil
          seul est nommément le défaut du bandeau d'ilygo-law, et un testeur
          arrive presque toujours par un lien profond.
          Hors UAT, le composant ne trouve pas de bandeau et ne rend rien. */}
      <ModaleUat />
      <Outlet />
    </>
  )
}

const avecDefilement = (enfants: RouteObject[]): RouteObject[] => [
  { element: <Racine />, children: enfants },
]

export const router = createBrowserRouter(avecDefilement(estHoteConsole() ? routesConsole : routes))
